import { useEffect, useState } from 'react';

import type { OnboardingStepProps } from '@pray/shared/components/onboarding/types';
import { useAuth } from '@pray/shared/hooks/useAuth';
import useForm from '@pray/shared/hooks/useForm';
import { navigate } from '@pray/shared/utils/navigation';

import { useStudioContext } from 'context/StudioContext';
import useAppRoutes from 'hooks/useAppRoutes';

import StudioLogo from './ui/StudioLogo';
import fallbackOnboardingSteps from './utils/fallback-steps';
import { injectNextSteps } from './utils/stepManagement';

export default function usePlgOnboardingFlow() {
  // const { data, isLoading, error } = useOnboardingSteps();
  const { data, isLoading, error } = { data: fallbackOnboardingSteps.data, isLoading: false, error: null };
  const { user } = useAuth();
  const { artists, isArtistLoading } = useStudioContext();
  const appRoute = useAppRoutes();

  const [steps, setSteps] = useState([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const isRegistered = !!user?.is_registered;
  // A registered account that already owns a leader does not belong in
  // onboarding, so it is sent to the app instead. `artists` is empty while the
  // list is in flight, which makes that answer untrustworthy until the list
  // settles: report the flow as loading for as long as the answer is unknown
  // (and while the redirect is pending) so no step is painted just to be
  // navigated away from a moment later. Visitors who are not registered yet
  // skip the check entirely, so a real signup still gets the first step with
  // no added wait.
  const hasExistingArtist = isRegistered && artists.length > 0;
  const isResolvingExistingAccount = isRegistered && (isArtistLoading || hasExistingArtist);

  useEffect(() => {
    if (hasExistingArtist) {
      navigate(appRoute.initial());
    }
  }, [hasExistingArtist]);

  const form = useForm({
    name: '',
    firstName: '',
    lastName: '',
    phone: '',
    countryCode: '',
    country: '',
    code: '',
  });

  // Initial default steps props
  const [stepProps, setStepProps] = useState<OnboardingStepProps>({
    showBackButton: false,
    showSkipButton: false,
    title: '',
    subtitle: '',
    logo: <StudioLogo />,
    hasLogo: false,
    hasAppBar: false,
    onContinue: () => {},
    onBack: () => {},
    form,
  });

  useEffect(() => {
    // Use fallback data if there's an error or use API data if successful
    const stepsData = error ? fallbackOnboardingSteps.data.entities : data?.entities || [];

    if (stepsData) {
      setSteps(stepsData.map((item) => item.type));
      setStepProps(mapStepsData(stepsData));
    }
  }, [data, error]);

  const moveForward = async ({ stepData = {}, isSkipped = false } = {}) => {
    const { steps: updatedSteps, stepProps: updatedStepProps } = injectNextSteps(
      steps[currentStepIndex],
      steps,
      stepProps,
      stepData,
      isSkipped
    );

    setSteps(updatedSteps);
    setStepProps(updatedStepProps);

    if (currentStepIndex < updatedSteps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      // Perform last step actions
      await handleLastStep();
    }
  };

  const moveBackward = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  const handleLastStep = async () => {
    window.location.href = appRoute.index();
  };

  return {
    form,
    currentStep: steps[currentStepIndex],
    currentStepProps: {
      ...stepProps[steps[currentStepIndex]],
      hasLogo: currentStepIndex === 0,
      hasAppBar:
        currentStepIndex === 0 ||
        stepProps[steps[currentStepIndex]]?.showBackButton ||
        stepProps[steps[currentStepIndex]]?.showSkipButton,
      logo: <StudioLogo />,
    },
    isLoading: isLoading || isResolvingExistingAccount,
    moveForward,
    moveBackward,
  };
}

// TODO: fix this type
const mapStepsData = (data) => {
  if (!data) return {} as OnboardingStepProps;

  return data.reduce((acc, item) => {
    acc[item.type] = item.properties;
    return acc;
  }, {}) as OnboardingStepProps;
};
